import Lenis from 'lenis';
import 'lenis/dist/lenis.css';

type Cleanup = () => void;
const noop: Cleanup = () => undefined;

/**
 * Public-surface wheel/trackpad smoothing only.
 *
 * M4P contract:
 * - Lenis remains the only JavaScript scroll owner.
 * - autoRaf is disabled: the RAF loop exists only while Lenis is actively settling.
 * - no Lenis scroll subscriber, scroll-linked effect, layout read or Three.js work.
 * - Control keeps native scrolling.
 */
export const setupSmoothScroll = (reduced: MediaQueryList): Cleanup => {
  if (document.body.dataset.surface === 'control' || reduced.matches) return noop;

  const lenis = new Lenis({
    autoRaf: false,
    smoothWheel: true,
    anchors: {},
    lerp: 0.09,
    wheelMultiplier: 0.92,
    touchMultiplier: 1,
    syncTouch: false,
    overscroll: false,
    autoResize: true,
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    prevent: (node) => node instanceof HTMLElement && Boolean(node.closest('[data-lenis-prevent]')),
  });

  let frame = 0;
  let idleFrames = 0;

  const stopFrame = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    idleFrames = 0;
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(tick);
  };

  const tick = (time: number) => {
    frame = 0;
    lenis.raf(time);
    if (lenis.isScrolling) {
      idleFrames = 0;
      schedule();
      return;
    }
    // One confirmation frame avoids stopping between the final two interpolation steps.
    if (idleFrames < 1) {
      idleFrames += 1;
      schedule();
      return;
    }
    idleFrames = 0;
  };

  const kick = () => {
    idleFrames = 0;
    schedule();
  };

  const onWheel = () => kick();
  const onAnchor = (event: MouseEvent) => {
    const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href^="#"]') : null;
    if (anchor) kick();
  };
  const stopForRoute = () => {
    lenis.stop();
    stopFrame();
  };
  const resume = () => {
    if (document.visibilityState === 'hidden' || reduced.matches) return;
    lenis.resize();
    lenis.start();
    kick();
  };
  const visibility = () => {
    if (document.visibilityState === 'hidden') {
      lenis.stop();
      stopFrame();
    } else resume();
  };
  const motionPreference = () => {
    if (reduced.matches) {
      lenis.stop();
      stopFrame();
    } else resume();
  };

  window.addEventListener('wheel', onWheel, { passive: true, capture: true });
  document.addEventListener('click', onAnchor, { passive: true });
  window.addEventListener('kbuild:route-leave', stopForRoute as EventListener);
  window.addEventListener('pageshow', resume);
  document.addEventListener('visibilitychange', visibility);
  reduced.addEventListener('change', motionPreference);

  return () => {
    window.removeEventListener('wheel', onWheel, true);
    document.removeEventListener('click', onAnchor);
    window.removeEventListener('kbuild:route-leave', stopForRoute as EventListener);
    window.removeEventListener('pageshow', resume);
    document.removeEventListener('visibilitychange', visibility);
    reduced.removeEventListener('change', motionPreference);
    stopFrame();
    lenis.destroy();
  };
};
